import React, { useState, useCallback, useEffect } from "react";
import { Button, Spinner } from "react-bootstrap";
import Dropdown from "react-bootstrap/Dropdown";
import search from "../../../assets/Vehicles/search.png";
import { Plus } from "lucide-react";
import close from "../../../assets/Vehicles/close_filter.png";
import fill_chevron_down from "../../../assets/Vehicles/fill_chevron_down.png";

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";

import "./List.css";
import { useNavigate } from "react-router-dom";
import { geVehicleListApi } from "../vehicle-api-new.js";
import Pagination from "../../common/pagination";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import edit from "../../../assets/svg/edit.svg";
import del from "../../../assets/svg/delete.svg";
import DeleteModal from "../../common/delete-modal.js";
import { Toast, ToastContainer } from "react-bootstrap";
import CommonDropdown from "../../common/common_dropDown";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import DatePicker from "react-datepicker";
import { Calendar } from "lucide-react";
import { useGlobal } from "../../../GlobalContext.js";

export const List = () => {
  const apiUrl = process.env.REACT_APP_API_URL;

  const { rolePermissionList } = useGlobal();

  const columns = [
    {
      header: "Name",
      accessorKey: "name",
      cell: (info) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "200px",
          }}
        >
          {info.getValue()}
        </div>
      ),
    },

    {
      header: "VIN",
      accessorKey: "vin",
      cell: (info) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "200px",
          }}
        >
          {info.getValue()}
        </div>
      ),
    },
    {
      header: "License Plate",
      accessorKey: "lic_no",
      cell: (info) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "150px",
          }}
        >
          {info.getValue()}
        </div>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
    },
    {
      header: "Type",
      accessorKey: "type",
      cell: (info) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "150px",
          }}
        >
          {info.getValue()}
        </div>
      ),
    },
    {
      header: "Group",
      accessorKey: "group",
      cell: (info) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "150px",
          }}
        >
          {info.getValue()}
        </div>
      ),
    },

    {
      header: "Current KM",
      accessorKey: "km",
    },
    {
      header: "Entry Date",
      accessorKey: "entry_date",
      cell: (info) => {
        return (
          <div
            className="hover-action-cell"
            style={{
              maxWidth: "150px",
            }}
          >
            <div
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {info.getValue()}
            </div>
            <div className="click-actions">
              {checkPermission("Vehicle", "edit_permission") && (
                <button
                  className="icon-btn"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent triggering row click
                    navigate("/vehicle-create", {
                      state: { id: info.row.original.id },
                    });
                  }}
                >
                  <img src={edit} alt="edit" />
                </button>
              )}
              {checkPermission("Vehicle", "delete_permission") && (
                <button
                  className="icon-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteId(info.row.original.id);
                  }}
                >
                  <img src={del} alt="delete" />
                </button>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  const navigate = useNavigate();

  // Global filter (search)
  const [globalFilter, setGlobalFilter] = useState("");
  const [sortByValue, setSortByValue] = useState({
    value: "created desc",
    label: "Created (Newest)",
  });
  const [sortByField, setSortByField] = useState("created");
  const [sortByDir, setSortByDir] = useState("desc");

  const [typeOption, setTypeOption] = useState({ value: "all", label: "All" });
  const [vehicleTypeOption, setVehicleTypeOption] = useState(null);
  const [groupFilter, setGroupFilter] = useState("");

  const [selectedDate, setSelectedDate] = useState();
  const sortByList = [
    {
      value: "created asc",
      label: "Created (Oldest)",
    },
    { value: "created desc", label: "Created (Newest)" },
    { value: "updated asc", label: "Updated Date (Oldest)" },
    { value: "updated desc", label: "Updated Date (Newest)" },
  ];
  const typeList = [
    { value: "all", label: "All" },
    { value: "assigned", label: "Assigned" },
    { value: "unassigned", label: "Unassigned" },
  ];

  const [vehicleList, setVehicleList] = useState([]);
  const [vehicleCount, setVehicleCount] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const handleSelectVehicleType = (item) => {
    setVehicleTypeOption(item);
    setItemOffset(0);
  };

  const handleRowClick = (rowData) => {
    navigate("/vehicle-view", { state: rowData });
  };

  useEffect(() => {
    geVehicleList();
  }, [
    itemsPerPage,
    itemOffset,
    globalFilter,
    sortByValue,
    typeOption,
    vehicleTypeOption,
    selectedDate,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      geVehicleList();
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler); // cleanup old timer
    };
  }, [groupFilter]);

  const geVehicleList = async () => {
    setLoading(true);
    try {
      const response = await geVehicleListApi(
        `search=${globalFilter}&limit=${itemsPerPage}&skip=${itemOffset * itemsPerPage}&sort_field=${sortByField}&sort_dir=${sortByDir}&type=${typeOption != null ? typeOption.value : ""}&vehicle_type=${vehicleTypeOption != null ? vehicleTypeOption.value : ""}&group=${groupFilter}&date=${selectedDate != null && selectedDate !== "" ? selectedDate.toLocaleDateString("en-CA") : ""}`,
      );
      if (response.data.success === true) {
        setVehicleCount(response.data.total_rec);
        setVehicleList(response.data.vehicles);
        setPageCount(response.data.total_pgs);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
      setLoading(false);
    }
  };
  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);

  const table = useReactTable({
    data: vehicleList ?? [],
    columns,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(null);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  useEffect(() => {
    if (deleteId != null && deleteId !== 0) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const cancelDelete = () => {
    setDeleteId(null);
    setIsOpenDelete(false);
  };
  const onSuccessDelete = (response) => {
    setDeleteId(null);
    setSnackbarMessage(response.message);
    setSnackbarStatus(response.success);
    handleSnackbarShow();
    if (response.success === true) {
      setIsOpenDelete(false);
      geVehicleList();
    }
  };
  function checkPermission(menuName, permission) {
    return rolePermissionList.findIndex(
      (element) =>
        element["name"] === menuName &&
        element["menu_permission"] === true &&
        element[permission] === true,
    ) > -1
      ? true
      : false;
  }
  return (
    <>
      {isOpenDelete && (
        <DeleteModal
          deleteUrl={`vehicle/delete/${deleteId}`}
          onSuccess={onSuccessDelete}
          onCancel={cancelDelete}
        />
      )}
      <div className="bg-half-white">
        <div className=" rounded-4 mx-3 my-2" style={{ background: "#FFFFFF" }}>
          <div className="d-flex p-3 flex-wrap align-items-center justify-content-between">
            <div className="d-flex flex-column">
              <p className="bg-transparent fw-bold dark-purple border-0 fs-24 assigned-dropdowns m-0">
                Vehicle
              </p>

              <p className="text-muted">{vehicleCount} records</p>
            </div>
            <div className="d-flex flex-wrap gap-3">
              <div className="search-wrapper d-flex align-items-center gap-2 bg-white rounded-3 px-3 py-2">
                <img src={search} alt="search" />
                <input
                  type="text"
                  placeholder="Search"
                  className="border-0 outline-0 rounded-3"
                  value={globalFilter || ""}
                  onChange={(e) => {
                    setGlobalFilter(e.target.value);
                    setItemOffset(0);
                  }}
                />
              </div>
              {checkPermission("Vehicle", "create_permission") && (
                <Button
                  className="dark-purple-btn border-0 d-flex gap-1 px-3 rounded-3 text-white"
                  onClick={() => {
                    navigate("/vehicle-create");
                  }}
                >
                  <Plus />
                  Add vehicle
                </Button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="d-flex flex-wrap align-items-center justify-content-between p-3 gap-2">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <p className="m-0 text-filter-grey fw-semibold">Filter :</p>
              <Dropdown
                data-tooltip-id="sort-tooltip"
                data-tooltip-content="Sort by"
              >
                <Dropdown.Toggle
                  id="dropdown-basic"
                  className="bg-white text-purple search-border assigned-dropdown rounded-4 px-4 d-flex align-items-center gap-2"
                >
                  {sortByValue.label}
                  <img src={fill_chevron_down} alt="chev down" />
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ minWidth: "unset", width: "100%" }}>
                  {sortByList.map((item) => (
                    <Dropdown.Item
                      onClick={() => {
                        const arr = item.value.split(" ");
                        if (arr.length > 1) {
                          setSortByField(arr[0]);
                          setSortByDir(arr[1]);
                        }
                        setSortByValue(item);
                        setItemOffset(0);
                      }}
                    >
                      {item.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
                <Tooltip id="sort-tooltip" place="top" />
              </Dropdown>

              <Dropdown
                data-tooltip-id="type-tooltip"
                data-tooltip-content="Type"
              >
                <Dropdown.Toggle
                  id="dropdown-basic"
                  className="bg-white text-purple search-border assigned-dropdown rounded-4 px-4 d-flex align-items-center gap-2"
                >
                  {typeOption?.label ?? "Type"}{" "}
                  <img src={fill_chevron_down} alt="chev down" />{" "}
                  {typeOption != null && (
                    <img
                      onClick={() => {
                        setTypeOption(null);
                        setItemOffset(0);
                      }}
                      src={close}
                      alt="x"
                    />
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ minWidth: "unset", width: "100%" }}>
                  {typeList.map((item) => (
                    <Dropdown.Item
                      onClick={() => {
                        setTypeOption(item);
                        setItemOffset(0);
                      }}
                    >
                      {item.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
                <Tooltip id="type-tooltip" place="top" />
              </Dropdown>

              <div
                className="w-15 w-md-25 w-lg-18 d-flex"
                style={{ maxWidth: "160px" }}
                data-tooltip-id="vtype-tooltip"
                data-tooltip-content="Vehicle Type"
              >
                <CommonDropdown
                  apiUrl={`${apiUrl}get_vehicletype`}
                  placeholder="Vehicle Type"
                  value={vehicleTypeOption}
                  onSelect={handleSelectVehicleType}
                  searchView={true}
                />

                <Tooltip id="vtype-tooltip" place="top" />
              </div>

              <div
                className="w-15 w-md-25 w-lg-18 d-flex align-items-center bg-white text-purple search-border rounded-4"
                style={{ maxWidth: "180px" }}
                data-tooltip-id="date-tooltip"
                data-tooltip-content="Date"
              >
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => {
                    setSelectedDate(date);
                    setItemOffset(0);
                  }}
                  dateFormat="dd-mm-yyyy"
                  customInput={
                    <button
                      style={{ color: "#8790A1" }}
                      className="border-0 form-control vehicle-form-input bg-white rounded-4 px-3 py-2 d-flex justify-content-between align-items-center gap-2"
                    >
                      {selectedDate ? (
                        selectedDate
                          .toLocaleDateString("en-GB")
                          .replace(/\//g, "-")
                      ) : (
                        <span style={{ color: "#8790A1" }}>dd-mm-yyyy</span>
                      )}
                      <Calendar size={16} />
                    </button>
                  }
                />
                <Tooltip id="date-tooltip" place="top" />
                {selectedDate != null && (
                  <img
                    onClick={() => {
                      setSelectedDate(null);
                      setItemOffset(0);
                    }}
                    src={close}
                    alt="x"
                    style={{ height: "30px" }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="p-3">
            <div className="table-wrapper overflow-x-auto">
              <table>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-3 py-2"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {header.column.getIsSorted() === "asc" && " asc"}
                          {header.column.getIsSorted() === "desc" && " des"}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="text-center loading-cell"
                      >
                        <Spinner
                          animation="border"
                          className="text-dark-purple"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      </td>
                    </tr>
                  ) : table.getRowModel().rows.length > 0 ? (
                    table.getRowModel().rows.map((row, index) => (
                      <tr
                        key={row.id}
                        onClick={() => handleRowClick(row.original)}
                        className="vehicle-table-row"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="px-3 ">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="text-center empty-cell text-muted border-none"
                      >
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              pageCount={pageCount}
              handlePageClick={handlePageClick}
              onPageSizeChange={(size) => table.setPageSize(size)}
              currentPage={itemOffset}
            />
          </div>
        </div>
      </div>
      <ToastContainer position="top-end">
        <Toast
          className="d-inline-block m-1"
          bg={snackbarStatus === true ? "success" : "danger"}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
        >
          <Toast.Body style={{ color: "#ffffff" }}>
            {snackbarMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};
